import http from './http'

function mapPage (res) {
  var page = res.data
  return {
    items: page.content || [],
    total: page.totalElements || 0,
    hasMore: !page.last
  }
}

// ─── Client Actions ───────────────────────────────────────────────────────────

/**
 * Get presigned PUT URLs for all stem files in one request.
 * @param {{ projectName: string, jobType?, genre?, referenceTrack?, clientNotes?, files: Array<{ name: string, contentType: string }> }} payload
 *   jobType/genre/referenceTrack/clientNotes are optional — mirrors the rest of the job
 *   form so it can be restored via getDraftUploads() if the submission never gets
 *   finished. Not otherwise used by this endpoint.
 * @returns {Promise<Array<{ name: string, objectKey: string, uploadUrl: string }>>}
 */
export function getBatchUploadUrls (payload) {
  return http.post('/jobs/upload-urls', payload).then(function (res) {
    var data = res.data
    return Array.isArray(data) ? data : (data.uploads || data)
  })
}

/**
 * Files already uploaded under this user's account that were never attached to a
 * job (submission never completed), grouped by project. Lets a client offer to
 * resume/finish a submission from a device other than the one that did the upload.
 * @returns {Promise<Array<{ projectSlug, files: Array<{name, objectKey, sizeBytes}>, totalBytes, updatedAt }>>}
 */
export function getDraftUploads () {
  return http.get('/jobs/drafts').then(function (res) {
    return res.data
  })
}

/**
 * Create a mixing job in UPLOADED status.
 * All stem files must already be in MinIO before calling this.
 * jobType: MIXING | MASTERING | MIXING_AND_MASTERING
 * @param {{ jobType, projectName, genre, referenceTrack, clientNotes, inputFileKeys: string[] }} payload
 */
export function createJob (payload) {
  return http.post('/jobs', payload).then(function (res) {
    return res.data
  })
}

/**
 * List all mixing jobs belonging to the authenticated client (paginated).
 * @param {{ page?: number, size?: number }} params
 */
export function getMyJobs (params) {
  var query = {
    page: params && params.page > 1 ? params.page - 1 : 0,
    size: (params && params.size) || 20
  }
  return http.get('/jobs/my', { params: query }).then(mapPage)
}

/**
 * Get full job detail. Preview URL present only when status >= PREVIEW_READY.
 * Full output URL present only when status is PAID or DELIVERED.
 * @param {string} jobId
 */
export function getJob (jobId) {
  return http.get('/jobs/' + jobId).then(function (res) {
    return res.data
  })
}

/**
 * Approve the engineer's preview. Moves job to AWAITING_PAYMENT.
 * Job must be in PREVIEW_READY status with a quote submitted.
 * @param {string} jobId
 */
export function approvePreview (jobId) {
  return http.post('/jobs/' + jobId + '/approve-preview').then(function (res) {
    return res.data
  })
}

/**
 * Request a revision on the current preview.
 * Job must be in PREVIEW_READY status. Max 2 revisions per job.
 * @param {string} jobId
 * @param {{ notes?: string }} payload
 */
export function requestRevision (jobId, payload) {
  return http.post('/jobs/' + jobId + '/request-revision', payload || {}).then(function (res) {
    return res.data
  })
}

/**
 * Cancel a job. Not allowed once status is PAID or DELIVERED.
 * @param {string} jobId
 */
export function cancelJob (jobId) {
  return http.post('/jobs/' + jobId + '/cancel').then(function (res) {
    return res.data
  })
}

// ─── Engineer Actions ─────────────────────────────────────────────────────────

/**
 * List all jobs assigned to the authenticated engineer (paginated).
 * Requires ENGINEER or ADMIN role.
 * @param {{ page?: number, size?: number }} params
 */
export function getEngineerJobs (params) {
  var query = {
    page: params && params.page > 1 ? params.page - 1 : 0,
    size: (params && params.size) || 20
  }
  return http.get('/jobs/engineer', { params: query }).then(mapPage)
}

/**
 * Accept the assignment and move job from PENDING_ASSIGNMENT to IN_PROGRESS.
 * @param {string} jobId
 */
export function startJob (jobId) {
  return http.post('/jobs/' + jobId + '/start').then(function (res) {
    return res.data
  })
}

/**
 * Set the price for the job. Can be updated until the client approves.
 * @param {string} jobId
 * @param {{ amountKes: number }} payload
 */
export function submitQuote (jobId, payload) {
  return http.post('/jobs/' + jobId + '/quote', payload).then(function (res) {
    return res.data
  })
}

/**
 * Get a presigned PUT URL for uploading the preview mix for a specific job.
 * @param {string} jobId
 * @param {string} filename  e.g. "track-preview.mp3"
 * @returns {Promise<{ url: string, objectKey: string }>}
 */
export function getPreviewUploadUrl (jobId, filename) {
  return http.get('/jobs/' + jobId + '/preview-upload-url', { params: { filename: filename } }).then(function (res) {
    return res.data
  })
}

/**
 * Get a presigned PUT URL for uploading the final delivery file for a specific job.
 * @param {string} jobId
 * @param {string} filename  e.g. "track-final.wav"
 * @returns {Promise<{ url: string, objectKey: string }>}
 */
export function getDeliveryUploadUrl (jobId, filename) {
  return http.get('/jobs/' + jobId + '/delivery-upload-url', { params: { filename: filename } }).then(function (res) {
    return res.data
  })
}

/**
 * Upload a preview mix and move job to PREVIEW_READY.
 * previewKey must already be in the mixing-outputs MinIO bucket.
 * Also valid from REVISION_IN_PROGRESS status.
 * @param {string} jobId
 * @param {{ previewKey: string }} payload
 */
export function submitPreview (jobId, payload) {
  return http.post('/jobs/' + jobId + '/submit-preview', payload).then(function (res) {
    return res.data
  })
}

/**
 * Acknowledge a revision request and move job from REVISION_REQUESTED to REVISION_IN_PROGRESS.
 * @param {string} jobId
 */
export function startRevision (jobId) {
  return http.post('/jobs/' + jobId + '/start-revision').then(function (res) {
    return res.data
  })
}

/**
 * Upload the final output and move job to DELIVERED.
 * Job must be in PAID status. fullOutputKey must be in mixing-outputs bucket.
 * @param {string} jobId
 * @param {{ fullOutputKey: string }} payload
 */
export function deliverOutput (jobId, payload) {
  return http.post('/jobs/' + jobId + '/deliver', payload).then(function (res) {
    return res.data
  })
}

/**
 * Update engineer's internal notes (visible to the client).
 * Can be updated at any point in the job lifecycle.
 * @param {string} jobId
 * @param {{ notes: string }} payload
 */
export function updateEngineerNotes (jobId, payload) {
  return http.patch('/jobs/' + jobId + '/engineer-notes', payload).then(function (res) {
    return res.data
  })
}

// ─── Multipart Upload (large files > 250 MB) ─────────────────────────────────

/**
 * Step 1: initiate a multipart upload for a large file.
 * @param {string} filename    Original filename including extension
 * @param {string} projectName Project name (used for MinIO path)
 * @param {{ jobType?, genre?, referenceTrack?, clientNotes? }} [formFields]
 *   Optional — mirrors the rest of the job form so it can be restored via
 *   getDraftUploads() if the submission never gets finished. Not otherwise used.
 * @returns {Promise<{ uploadId: string, objectKey: string }>}
 */
export function initiateMultipartUpload (filename, projectName, formFields) {
  var body = Object.assign({ filename: filename, projectName: projectName }, formFields || {})
  return http.post('/jobs/multipart/initiate', body).then(function (res) {
    return res.data
  })
}

/**
 * Step 2: get a presigned PUT URL for one chunk. Call once per part (1-based).
 * PUT the raw chunk bytes to the returned partUrl and collect the ETag response header.
 * @param {string} uploadId
 * @param {string} objectKey
 * @param {number} partNumber  1-based chunk index
 * @returns {Promise<{ partUrl: string }>}
 */
export function getPartUploadUrl (uploadId, objectKey, partNumber) {
  return http.get('/jobs/multipart/part-url', {
    params: { uploadId: uploadId, objectKey: objectKey, partNumber: partNumber }
  }).then(function (res) {
    return res.data
  })
}

/**
 * Step 3: finalise the multipart upload. Pass all (partNumber, etag) pairs from PUT responses.
 * @param {string} uploadId
 * @param {string} objectKey
 * @param {Array<{ partNumber: number, etag: string }>} parts
 * @returns {Promise<{ objectKey: string }>}
 */
export function completeMultipartUpload (uploadId, objectKey, parts) {
  return http.post('/jobs/multipart/complete', {
    uploadId: uploadId,
    objectKey: objectKey,
    parts: parts
  }).then(function (res) {
    return res.data
  })
}

// ─── Admin Actions ────────────────────────────────────────────────────────────

/**
 * List all jobs across all clients and engineers (ADMIN only, paginated).
 * @param {{ page?: number, size?: number, status?: string, sort?: string }} params
 */
export function listAllJobs (params) {
  var query = {
    page: params && params.page > 1 ? params.page - 1 : 0,
    size: (params && params.size) || 20
  }
  if (params && params.status) query.status = params.status
  if (params && params.sort) query.sort = params.sort
  return http.get('/jobs', { params: query }).then(mapPage)
}

/**
 * Assign an engineer to a job in UPLOADED status (ADMIN only).
 * Moves job to PENDING_ASSIGNMENT and notifies the client.
 * @param {string} jobId
 * @param {{ engineerUserId: string }} payload
 */
export function assignEngineer (jobId, payload) {
  return http.post('/jobs/' + jobId + '/assign', payload).then(function (res) {
    return res.data
  })
}

// ─── Messaging ────────────────────────────────────────────────────────────────

/**
 * Send a message on the job thread.
 * Optionally attach a file via fileKey (MinIO object key, must already be uploaded).
 * @param {string} jobId
 * @param {{ message: string, fileKey?: string|null }} payload
 */
export function sendMessage (jobId, payload) {
  return http.post('/jobs/' + jobId + '/messages', payload).then(function (res) {
    return res.data
  })
}

/**
 * Get all messages on the job thread in chronological order.
 * File attachments are returned as 60-min presigned MinIO GET URLs.
 * @param {string} jobId
 * @returns {Promise<Array<{ id, senderId, senderName, message, sentAt, fileUrl? }>>}
 */
export function getMessages (jobId) {
  return http.get('/jobs/' + jobId + '/messages').then(function (res) {
    var data = res.data
    return Array.isArray(data) ? data : (data.content || [])
  })
}
